import React from 'react';
import {
  ArrowRight,
  ArrowDownRight,
  ArrowDown,
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpLeft,
  ArrowUp,
  ArrowUpRight
} from 'lucide-react';
import { colors, typography } from '../../theme';

const PRESETS = [
  { angle: 0, Icon: ArrowRight },
  { angle: 45, Icon: ArrowDownRight },
  { angle: 90, Icon: ArrowDown },
  { angle: 135, Icon: ArrowDownLeft },
  { angle: 180, Icon: ArrowLeft },
  { angle: 225, Icon: ArrowUpLeft },
  { angle: 270, Icon: ArrowUp },
  { angle: 315, Icon: ArrowUpRight }
];

type Props = {
  value: number; // 0–360
  onPreview: (angle: number) => void; // while editing (live preview, no undo step)
  onCommit: (angle: number) => void; // on finish (blur or preset click, generates undo step)
};

export const AngleControl: React.FC<Props> = ({ value, onPreview, onCommit }) => {
  const [localValue, setLocalValue] = React.useState(value.toString());
  const [focused, setFocused] = React.useState(false);

  React.useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const handleChange = (raw: string) => {
    setLocalValue(raw);
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 360) {
      onPreview(parsed);
    }
  };

  const handleCommit = () => {
    const parsed = Number(localValue);
    if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 360) {
      onCommit(parsed);
    } else {
      setLocalValue(value.toString());
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(8, 1fr)',
          gap: 4,
          width: '100%'
        }}
      >
        {PRESETS.map(({ angle, Icon }) => {
          const isActive = value === angle;
          return (
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 27,
                border: `1px solid ${isActive ? colors.accent : colors.border}`,
                borderRadius: 4,
                background: isActive ? colors.accentMuted : colors.btnBg,
                color: isActive ? colors.accent : colors.textSecondary,
                cursor: 'pointer',
                padding: 0,
                transition: 'all 0.15s ease'
              }}
              key={angle}
              onClick={() => onCommit(angle)}
              title={`${angle}°`}
            >
              <Icon size={14} />
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            border: `1px solid ${focused ? colors.accent : colors.border}`,
            borderRadius: 4,
            background: focused ? colors.accentMuted : colors.btnBg,
            padding: '0 8px',
            width: 70,
            height: 27,
            transition: 'border-color 0.15s ease, background-color 0.15s ease'
          }}
        >
          <input
            type='text'
            inputMode='numeric'
            value={localValue}
            onChange={e => handleChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              handleCommit();
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleCommit();
                e.currentTarget.blur();
              }
            }}
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              width: '100%',
              fontSize: typography.fontSize.md,
              fontFamily: typography.fontFamily.ui,
              color: colors.textPrimary,
              textAlign: 'right',
              padding: 0
            }}
          />
          <span
            style={{
              fontSize: typography.fontSize.base,
              fontFamily: typography.fontFamily.ui,
              color: colors.textMuted,
              marginLeft: 2
            }}
          >
            °
          </span>
        </div>
      </div>
    </div>
  );
};
